import {
  ChevronRight,
  Phone,
  MessageSquare,
  Mail,
  MessageCircle,
  Megaphone,
  Target,
} from 'lucide-react';
import type { MobileCampaign } from './types';

interface CampaignCardProps {
  campaign: MobileCampaign;
  onTap: (campaign: MobileCampaign) => void;
}

const typeIcon: Record<MobileCampaign['type'], typeof Phone> = {
  outbound_call: Phone,
  sms: MessageSquare,
  whatsapp: MessageCircle,
  email: Mail,
  general: Megaphone,
  marketing: Megaphone,
  booking: Target,
  service: Target,
  gtm: Target,
  lead: Target,
};

const statusStyles: Record<MobileCampaign['status'], string> = {
  active: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  running: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  paused: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  draft: 'bg-muted text-muted-foreground',
  completed: 'bg-primary/15 text-primary',
  cancelled: 'bg-destructive/15 text-destructive',
};

export default function CampaignCard({ campaign, onTap }: CampaignCardProps) {
  const Icon = typeIcon[campaign.type] || Megaphone;
  const stats = campaign.activity_stats;
  // Mini-stats per spec: Contattati / Risposti / Convertiti.
  // When the activity-derived stats from /crm/dashboard-analytics are not
  // available (inactive/old campaigns), fall back to lead lifecycle counts:
  // - "contattati" ≈ leads totali generati (proxy)
  // - "risposti"  ≈ ignoto (mostriamo "—")
  // - "convertiti" ≈ leads won
  const contattati = stats?.contacted ?? campaign.leads_total ?? 0;
  const risposti: number | null = stats?.responded ?? null;
  const convertiti = stats?.converted ?? campaign.leads_won ?? 0;

  return (
    <button
      type="button"
      onClick={() => onTap(campaign)}
      className="w-full min-h-[44px] text-left rounded-xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition-all p-4 flex items-start gap-3"
      data-testid={`mobile-campaign-card-${campaign.id}`}
    >
      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>

      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-foreground truncate">{campaign.name}</span>
          <span
            className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${statusStyles[campaign.status] || statusStyles.draft}`}
          >
            {campaign.status}
          </span>
        </div>

        {campaign.description && (
          <p className="text-xs text-muted-foreground line-clamp-1">{campaign.description}</p>
        )}

        <div className="grid grid-cols-3 gap-2 pt-1.5">
          <MiniStat label="Contattati" value={contattati} />
          <MiniStat label="Risposti" value={risposti} />
          <MiniStat label="Convertiti" value={convertiti} />
        </div>
      </div>

      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
    </button>
  );
}

function MiniStat({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-md bg-muted/40 px-2 py-1.5 text-center">
      <div className="text-sm font-semibold text-foreground tabular-nums">
        {value === null ? '—' : value.toLocaleString('it-IT')}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
